/**
 * Ventana modal del backoffice.
 *
 * Usa el `<dialog>` del navegador con `showModal()` en vez de un `div` con
 * posicion fija. No es un detalle de gusto: el elemento nativo trae hecho lo
 * que casi siempre se olvida al construir un modal a mano —atrapa el tabulador
 * dentro de la ventana, cierra con Escape, deja inerte lo que queda detras y se
 * anuncia como dialogo a un lector de pantalla— sin una sola linea de teclado
 * propia ni una libreria.
 *
 * El cierre por Escape lo dispara el navegador con el evento `cancel`, y hay
 * que escucharlo para que el estado de React no quede creyendo que sigue
 * abierta.
 */
import { useEffect, useRef } from 'react';
import type { ReactNode } from 'react';

import { Icono } from '../comunes/Icono';

export interface ModalProps {
  titulo: string;
  bajada?: string;
  abierto: boolean;
  /**
   * `amplio` para lo que necesita dos columnas (una region con sus comunas);
   * `extra` para lo que ademas trae mucho que leer (el detalle de un pedido).
   */
  ancho?: 'normal' | 'amplio' | 'extra';
  onCerrar(): void;
  children: ReactNode;
}

export function Modal({
  titulo,
  bajada,
  abierto,
  ancho = 'normal',
  onCerrar,
  children,
}: ModalProps) {
  const dialogo = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const elemento = dialogo.current;
    if (elemento === null) return;

    // `showModal` y `close` son imperativos: abrir con el atributo `open` a
    // secas dejaria la ventana sin foco atrapado ni fondo inerte.
    if (abierto && !elemento.open) elemento.showModal();
    if (!abierto && elemento.open) elemento.close();
  }, [abierto]);

  return (
    <dialog
      ref={dialogo}
      className="modal"
      aria-labelledby="modal-titulo"
      onCancel={(evento) => {
        evento.preventDefault();
        onCerrar();
      }}
      /*
       * Un clic en el fondo llega al propio `<dialog>`, porque el contenido
       * vive en el `<div>` de adentro. Comparar el objetivo con el elemento es
       * lo que distingue "toco fuera" de "toco el formulario".
       */
      onClick={(evento) => {
        if (evento.target === dialogo.current) onCerrar();
      }}
    >
      <div className={ancho === 'normal' ? 'modal-caja' : `modal-caja ${ancho === 'amplio' ? 'amplia' : 'extra'}`}>
        <header className="modal-cab">
          <div>
            <h2 id="modal-titulo">{titulo}</h2>
            {bajada !== undefined ? <p className="sub">{bajada}</p> : null}
          </div>
          <button type="button" className="modal-cerrar" onClick={onCerrar} aria-label="Cerrar">
            <Icono nombre="cerrar" />
          </button>
        </header>

        <div className="modal-cuerpo">{children}</div>
      </div>
    </dialog>
  );
}
